import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface Apprenant {
  id_apprenant: string;
  nom: string;
  prenom: string;
}

export default function SatisfactionFinFormationPage() {
  const navigate = useNavigate();
  const [apprenants, setApprenants] = useState<Apprenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentSection, setCurrentSection] = useState(1);

  const [formData, setFormData] = useState({
    apprenant_id: '',
    nom: '',
    prenom: '',
    nom_naissance: '',
    nom_marital: '',
    
    // Contact DKBS
    comment_connu_dkbs: '',
    facilite_joindre: '',
    delai_reponse: '',
    accueil_dkbs: [] as string[],
    
    // Information avant formation
    info_objectifs: '',
    info_contenu: '',
    info_tarifs: '',
    info_acteurs: '',
    autre_info_souhaitee: '',
    satisfaction_accueil_globale: '',
    
    // Accompagnement
    point_besoins: '',
    parcours_adapte: '',
    constitution_dossiers: '',
    transmission_infos: '',
    outils_communication: '',
    satisfaction_accompagnement: '',
    
    // Organisation formation
    accueil_organisation: '',
    salles_locaux: '',
    materiel: '',
    
    // Contenu formation
    exemples_exercices: '',
    support_cours: '',
    rythme_progression: '',
    duree_formation: '',
    nombre_participants: '',
    
    // Formateurs
    competences_techniques: '',
    competences_pedagogiques: '',
    ecoute_disponibilite: '',
    
    // Objectifs et résultats
    objectif_formation: [] as string[],
    formation_interrompue: '',
    suggestions_commentaires: '',
    satisfaction_globale_formation: '',
    diplome_valide: '',
    
    // Nouvelles questions
    avance_projets: '',
    avance_projets_comment: '',
    creation_projet_associatif: '',
    creation_projet_ou_en_etes_vous: '',
    
    // Témoignage
    temoignage: ''
  });

  useEffect(() => {
    const isAuthorized = localStorage.getItem('satisfaction_fin_autorise') === 'true';
    if (!isAuthorized) {
      navigate('/login-satisfaction-fin');
      return;
    }

    loadApprenants();
  }, [navigate]);

  const loadApprenants = async () => {
    try {
      const { data, error } = await supabase
        .from('apprenants')
        .select('id_apprenant, nom, prenom')
        .order('nom', { ascending: true });

      if (error) throw error;
      setApprenants(data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des apprenants:', err);
      setError('Erreur lors du chargement des apprenants');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error } = await supabase
        .from('satisfaction_fin_formation')
        .insert([{
          apprenant_id: formData.apprenant_id,
          nom: formData.nom,
          prenom: formData.prenom,
          nom_naissance: formData.nom_naissance,
          nom_marital: formData.nom_marital,
          comment_connu_dkbs: formData.comment_connu_dkbs,
          facilite_joindre: formData.facilite_joindre,
          delai_reponse: formData.delai_reponse,
          accueil_dkbs: formData.accueil_dkbs.join(', '),
          info_objectifs: formData.info_objectifs,
          info_contenu: formData.info_contenu,
          info_tarifs: formData.info_tarifs,
          info_acteurs: formData.info_acteurs,
          autre_info_souhaitee: formData.autre_info_souhaitee,
          satisfaction_accueil_globale: formData.satisfaction_accueil_globale,
          point_besoins: formData.point_besoins,
          parcours_adapte: formData.parcours_adapte,
          constitution_dossiers: formData.constitution_dossiers,
          transmission_infos: formData.transmission_infos,
          outils_communication: formData.outils_communication,
          satisfaction_accompagnement: formData.satisfaction_accompagnement,
          accueil_organisation: formData.accueil_organisation,
          salles_locaux: formData.salles_locaux,
          materiel: formData.materiel,
          exemples_exercices: formData.exemples_exercices,
          support_cours: formData.support_cours,
          rythme_progression: formData.rythme_progression,
          duree_formation: formData.duree_formation,
          nombre_participants: formData.nombre_participants,
          competences_techniques: formData.competences_techniques,
          competences_pedagogiques: formData.competences_pedagogiques,
          ecoute_disponibilite: formData.ecoute_disponibilite,
          objectif_formation: formData.objectif_formation.join(', '),
          formation_interrompue: formData.formation_interrompue,
          suggestions_commentaires: formData.suggestions_commentaires,
          satisfaction_globale_formation: formData.satisfaction_globale_formation,
          diplome_valide: formData.diplome_valide,
          avance_projets: formData.avance_projets,
          avance_projets_comment: formData.avance_projets_comment,
          creation_projet_associatif: formData.creation_projet_associatif,
          creation_projet_ou_en_etes_vous: formData.creation_projet_ou_en_etes_vous,
          temoignage: formData.temoignage,
          date_soumission: new Date().toISOString()
        }]);

      if (error) throw error;

      localStorage.removeItem('satisfaction_fin_autorise');
      navigate('/merci-satisfaction-fin');
    } catch (err) {
      console.error('Erreur lors de l\'enregistrement:', err);
      setError('Erreur lors de l\'enregistrement. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const toggleAccueilOption = (option: string) => {
    setFormData(prev => ({
      ...prev,
      accueil_dkbs: prev.accueil_dkbs.includes(option)
        ? prev.accueil_dkbs.filter(o => o !== option)
        : [...prev.accueil_dkbs, option]
    }));
  };

  const toggleObjectifOption = (option: string) => {
    setFormData(prev => ({
      ...prev,
      objectif_formation: prev.objectif_formation.includes(option)
        ? prev.objectif_formation.filter(o => o !== option)
        : [...prev.objectif_formation, option]
    }));
  };

  const scaleOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
  const agreementOptions = ['Tout à fait', 'Plutôt', 'Pas vraiment', 'Pas du tout'];
  const agreementOptionsWithNA = ['Tout à fait', 'Plutôt', 'Pas vraiment', 'Pas du tout', 'Non concerné'];
  const accueilOptions = ['Courtois', 'Clair', 'Agréable', 'Efficace', 'Fiable', 'Correct', 'A l\'écoute', 'Mitigé', 'Désagréable', 'Confus', 'Expéditif', 'Froid'];
  const objectifOptions = [
    'Obtenir un titre ou un diplôme',
    'Changer de métier',
    'Obtenir un premier emploi ou retrouver un emploi',
    'Développer de nouvelles compétences en lien avec votre métier'
  ];

  const totalSections = 7;
  const progressPercentage = (currentSection / totalSections) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 mx-auto mb-4">
              <img 
                src="https://static.readdy.ai/image/4e4134bdead6558166f22511b6228c41/7838ad8dc199dc1e998c90a1f4b29262.png" 
                alt="DKBS Formation & Consulting" 
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Questionnaire de Satisfaction Fin de Formation
            </h1>
            <p className="text-gray-600">
              Votre avis nous aide à améliorer nos formations
            </p>
          </div>

          {/* Barre de progression */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Section {currentSection} / {totalSections}</span>
              <span className="text-sm font-medium text-purple-600">{Math.round(progressPercentage)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-purple-600 to-pink-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start space-x-3">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-600 text-sm"></i>
              </div>
              <p className="text-sm text-red-700 flex-1">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Section 1: Identité */}
            {currentSection === 1 && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Identité</h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Sélectionnez votre nom *
                      </label>
                      <select
                        value={formData.apprenant_id}
                        onChange={(e) => {
                          const selected = apprenants.find(a => a.id_apprenant === e.target.value);
                          setFormData(prev => ({ 
                            ...prev, 
                            apprenant_id: e.target.value,
                            nom: selected?.nom || '',
                            prenom: selected?.prenom || ''
                          }));
                        }}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                      >
                        <option value="">Sélectionnez votre nom</option>
                        {apprenants.map(apprenant => (
                          <option key={apprenant.id_apprenant} value={apprenant.id_apprenant}>
                            {apprenant.prenom} {apprenant.nom}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nom de naissance
                      </label>
                      <input
                        type="text"
                        value={formData.nom_naissance}
                        onChange={(e) => setFormData(prev => ({ ...prev, nom_naissance: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Votre nom de naissance"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nom marital
                      </label>
                      <input
                        type="text"
                        value={formData.nom_marital}
                        onChange={(e) => setFormData(prev => ({ ...prev, nom_marital: e.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Votre nom marital"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 2: Contact avec DKBS */}
            {currentSection === 2 && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Contact avec DKBS</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Comment avez-vous connu DKBS ?
                      </label>
                      <textarea
                        value={formData.comment_connu_dkbs}
                        onChange={(e) => setFormData(prev => ({ ...prev, comment_connu_dkbs: e.target.value }))}
                        maxLength={500}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={3}
                        placeholder="Votre réponse..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.comment_connu_dkbs.length}/500 caractères
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Comment évalueriez-vous la facilité à joindre DKBS ?
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {scaleOptions.map(option => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, facilite_joindre: option }))}
                            className={`w-12 h-12 rounded-xl text-sm font-medium transition-all ${
                              formData.facilite_joindre === option
                                ? 'bg-blue-600 text-white shadow-lg'
                                : 'bg-white border border-gray-200 text-gray-700 hover:bg-blue-50'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Comment évalueriez-vous le délai de réponse ?
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {scaleOptions.map(option => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, delai_reponse: option }))}
                            className={`w-12 h-12 rounded-xl text-sm font-medium transition-all ${
                              formData.delai_reponse === option
                                ? 'bg-blue-600 text-white shadow-lg'
                                : 'bg-white border border-gray-200 text-gray-700 hover:bg-blue-50'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Qu'avez-vous pensé de l'accueil que vous avez reçu chez DKBS ? (4 choix possibles, 1 minimum)
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {accueilOptions.map(option => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => toggleAccueilOption(option)}
                            disabled={!formData.accueil_dkbs.includes(option) && formData.accueil_dkbs.length >= 4}
                            className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                              formData.accueil_dkbs.includes(option)
                                ? 'bg-blue-600 text-white shadow-lg'
                                : 'bg-white border border-gray-200 text-gray-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {formData.accueil_dkbs.length}/4 choix sélectionnés
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 3: Information avant formation */}
            {currentSection === 3 && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Avant d'entrer en formation</h2>
                  <p className="text-sm text-gray-600 mb-6">Avez-vous eu une information claire sur :</p>
                  
                  <div className="space-y-5">
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-sm font-medium text-gray-800 mb-3">Les objectifs de la formation (obtention d'un diplôme, découverte des métiers…)</p>
                      <div className="grid grid-cols-2 gap-2">
                        {agreementOptionsWithNA.map(option => (
                          <label key={option} className="flex items-center space-x-2 cursor-pointer">
                            <div className="relative flex items-center justify-center w-5 h-5">
                              <input
                                type="radio"
                                name="info_objectifs"
                                value={option}
                                checked={formData.info_objectifs === option}
                                onChange={(e) => setFormData({...formData, info_objectifs: e.target.value})}
                                className="w-5 h-5 opacity-0 absolute cursor-pointer z-10"
                                required
                              />
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                formData.info_objectifs === option ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300'
                              }`}>
                                {formData.info_objectifs === option && (
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                              </div>
                            </div>
                            <span className="text-sm text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-sm font-medium text-gray-800 mb-3">Le contenu de la formation et le planning</p>
                      <div className="grid grid-cols-2 gap-2">
                        {agreementOptionsWithNA.map(option => (
                          <label key={option} className="flex items-center space-x-2 cursor-pointer">
                            <div className="relative flex items-center justify-center w-5 h-5">
                              <input
                                type="radio"
                                name="info_contenu"
                                value={option}
                                checked={formData.info_contenu === option}
                                onChange={(e) => setFormData({...formData, info_contenu: e.target.value})}
                                className="w-5 h-5 opacity-0 absolute cursor-pointer z-10"
                                required
                              />
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                formData.info_contenu === option ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300'
                              }`}>
                                {formData.info_contenu === option && (
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                              </div>
                            </div>
                            <span className="text-sm text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-sm font-medium text-gray-800 mb-3">Les tarifs</p>
                      <div className="grid grid-cols-2 gap-2">
                        {agreementOptionsWithNA.map(option => (
                          <label key={option} className="flex items-center space-x-2 cursor-pointer">
                            <div className="relative flex items-center justify-center w-5 h-5">
                              <input
                                type="radio"
                                name="info_tarifs"
                                value={option}
                                checked={formData.info_tarifs === option}
                                onChange={(e) => setFormData({...formData, info_tarifs: e.target.value})}
                                className="w-5 h-5 opacity-0 absolute cursor-pointer z-10"
                                required
                              />
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                formData.info_tarifs === option ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300'
                              }`}>
                                {formData.info_tarifs === option && (
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                              </div>
                            </div>
                            <span className="text-sm text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-sm font-medium text-gray-800 mb-3">Les acteurs de votre formation et leurs missions</p>
                      <div className="grid grid-cols-2 gap-2">
                        {agreementOptionsWithNA.map(option => (
                          <label key={option} className="flex items-center space-x-2 cursor-pointer">
                            <div className="relative flex items-center justify-center w-5 h-5">
                              <input
                                type="radio"
                                name="info_acteurs"
                                value={option}
                                checked={formData.info_acteurs === option}
                                onChange={(e) => setFormData({...formData, info_acteurs: e.target.value})}
                                className="w-5 h-5 opacity-0 absolute cursor-pointer z-10"
                                required
                              />
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                formData.info_acteurs === option ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300'
                              }`}>
                                {formData.info_acteurs === option && (
                                  <div className="w-2 h-2 bg-white rounded-full"></div>
                                )}
                              </div>
                            </div>
                            <span className="text-sm text-gray-700">{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Quelle autre information auriez-vous souhaité connaître avant votre entrée en formation ?
                      </label>
                      <textarea
                        value={formData.autre_info_souhaitee}
                        onChange={(e) => {
                          if (e.target.value.length <= 500) {
                            setFormData({...formData, autre_info_souhaitee: e.target.value});
                          }
                        }}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        rows={3}
                        placeholder="Précisez..."
                        maxLength={500}
                      />
                      <p className="text-xs text-gray-500 mt-1 text-right">{formData.autre_info_souhaitee.length}/500 caractères</p>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-4 mt-6">
                    <label className="block text-sm font-medium text-gray-800 mb-3">
                      Satisfaction globale sur l'accueil de DKBS
                    </label>
                    <div className="flex justify-between items-center">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                        <label key={num} className="flex flex-col items-center cursor-pointer">
                          <div className="relative flex items-center justify-center w-10 h-10">
                            <input
                              type="radio"
                              name="satisfaction_accueil"
                              value={num}
                              checked={formData.satisfaction_accueil_globale === String(num)}
                              onChange={(e) => setFormData({...formData, satisfaction_accueil_globale: e.target.value})}
                              className="w-10 h-10 opacity-0 absolute cursor-pointer z-10"
                              required
                            />
                            <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all ${
                              formData.satisfaction_accueil_globale === String(num) ? 'bg-blue-500 border-blue-500 text-white' : 'bg-white border-gray-300 text-gray-700'
                            }`}>
                              <span className="text-sm font-semibold">{num}</span>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                    <div className="flex justify-between mt-2">
                      <span className="text-xs text-gray-600">Pas satisfait</span>
                      <span className="text-xs text-gray-600">Très satisfait</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 4: Accompagnement / Suivi */}
            {currentSection === 4 && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Accompagnement / Suivi</h2>
                  
                  <div className="space-y-6">
                    {[
                      { key: 'point_besoins', label: 'A-t-il fait un point avec vous sur vos besoins de formation en lien avec votre projet et vos expériences ?' },
                      { key: 'parcours_adapte', label: 'Votre parcours de formation a-t-il été adapté à votre besoin ?' }
                    ].map((question) => (
                      <div key={question.key}>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          {question.label}
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                          {agreementOptionsWithNA.map(option => (
                            <button
                              key={option}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, [question.key]: option }))}
                              className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                                formData[question.key as keyof typeof formData] === option
                                  ? 'bg-orange-600 text-white shadow-lg'
                                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-orange-50'
                              }`}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}

                    <div className="border-t border-gray-200 pt-6">
                      <p className="text-sm font-semibold text-gray-700 mb-6">
                        Qu'avez-vous pensé de l'organisation administrative et de l'accompagnement lors de votre formation ?
                      </p>
                      
                      {[
                        { key: 'constitution_dossiers', label: 'Constitution et suivi des dossiers' },
                        { key: 'transmission_infos', label: 'Transmission des informations' },
                        { key: 'outils_communication', label: 'Outils de communication' }
                      ].map((question) => (
                        <div key={question.key} className="mb-6">
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            {question.label}
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {scaleOptions.map(option => (
                              <button
                                key={option}
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, [question.key]: option }))}
                                className={`w-12 h-12 rounded-xl text-sm font-medium transition-all ${
                                  formData[question.key as keyof typeof formData] === option
                                    ? 'bg-orange-600 text-white shadow-lg'
                                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-orange-50'
                                }`}
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Satisfaction globale sur l'accompagnement / suivi
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {scaleOptions.map(option => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, satisfaction_accompagnement: option }))}
                            className={`w-12 h-12 rounded-xl text-sm font-medium transition-all ${
                              formData.satisfaction_accompagnement === option
                                ? 'bg-orange-600 text-white shadow-lg'
                                : 'bg-white border border-gray-200 text-gray-700 hover:bg-orange-50'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 5: Organisation et contenu de la formation */}
            {currentSection === 5 && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Organisation de la formation</h2>
                  
                  <div className="space-y-6">
                    {[
                      { key: 'accueil_organisation', label: 'Accueil / Organisation' },
                      { key: 'salles_locaux', label: 'Salles de formation / Locaux' },
                      { key: 'materiel', label: 'Matériel mis à disposition' }
                    ].map((question) => (
                      <div key={question.key}>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          {question.label}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {scaleOptions.map(option => (
                            <button
                              key={option}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, [question.key]: option }))}
                              className={`w-12 h-12 rounded-xl text-sm font-medium transition-all ${
                                formData[question.key as keyof typeof formData] === option
                                  ? 'bg-teal-600 text-white shadow-lg'
                                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-teal-50'
                              }`}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Contenu de la formation</h2>
                  
                  <div className="space-y-6">
                    {[
                      { key: 'exemples_exercices', label: 'Choix des exemples et exercices' },
                      { key: 'support_cours', label: 'Support de cours' },
                      { key: 'rythme_progression', label: 'Rythme de progression' },
                      { key: 'duree_formation', label: 'Durée de la formation' },
                      { key: 'nombre_participants', label: 'Nombre de participants' }
                    ].map((question) => (
                      <div key={question.key}>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          {question.label}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {scaleOptions.map(option => (
                            <button
                              key={option}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, [question.key]: option }))}
                              className={`w-12 h-12 rounded-xl text-sm font-medium transition-all ${
                                formData[question.key as keyof typeof formData] === option
                                  ? 'bg-violet-600 text-white shadow-lg'
                                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-violet-50'
                              }`}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Section 6: Formateurs et objectifs */}
            {currentSection === 6 && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Les formateurs</h2>
                  
                  <div className="space-y-6">
                    {[
                      { key: 'competences_techniques', label: 'Compétences techniques' },
                      { key: 'competences_pedagogiques', label: 'Compétences pédagogiques' },
                      { key: 'ecoute_disponibilite', label: 'Leur écoute et disponibilité' }
                    ].map((question) => (
                      <div key={question.key}>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          {question.label}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {scaleOptions.map(option => (
                            <button
                              key={option}
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, [question.key]: option }))}
                              className={`w-12 h-12 rounded-xl text-sm font-medium transition-all ${
                                formData[question.key as keyof typeof formData] === option
                                  ? 'bg-rose-600 text-white shadow-lg'
                                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-rose-50'
                              }`}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Votre objectif</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Quel était votre objectif en entrant en formation ?
                      </label>
                      <div className="space-y-2">
                        {objectifOptions.map(option => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => toggleObjectifOption(option)}
                            className={`w-full px-4 py-3 rounded-xl text-sm font-medium text-left transition-all ${
                              formData.objectif_formation.includes(option)
                                ? 'bg-indigo-600 text-white shadow-lg'
                                : 'bg-white border border-gray-200 text-gray-700 hover:bg-indigo-50'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Avez-vous interrompu la formation avant la date de fin prévue ?
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {agreementOptions.map(option => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, formation_interrompue: option }))}
                            className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                              formData.formation_interrompue === option
                                ? 'bg-indigo-600 text-white shadow-lg'
                                : 'bg-white border border-gray-200 text-gray-700 hover:bg-indigo-50'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 7: Satisfaction globale et témoignage */}
            {currentSection === 7 && (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Satisfaction globale</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Avez-vous des suggestions ou commentaires supplémentaires à propos de la formation ?
                      </label>
                      <textarea
                        value={formData.suggestions_commentaires}
                        onChange={(e) => {
                          if (e.target.value.length <= 500) {
                            setFormData(prev => ({ ...prev, suggestions_commentaires: e.target.value }));
                          }
                        }}
                        maxLength={500}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                        rows={4}
                        placeholder="Vos suggestions..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.suggestions_commentaires.length}/500 caractères
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Satisfaction globale sur la formation *
                      </label>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-gray-600">Pas satisfait</span>
                        <span className="text-xs text-gray-600">Très satisfait</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {scaleOptions.map(option => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, satisfaction_globale_formation: option }))}
                            className={`w-12 h-12 rounded-xl text-sm font-medium transition-all ${
                              formData.satisfaction_globale_formation === option
                                ? 'bg-yellow-600 text-white shadow-lg'
                                : 'bg-white border border-gray-200 text-gray-700 hover:bg-yellow-50'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Avez-vous validé votre diplôme/votre année de formation ? *
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {agreementOptions.map(option => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, diplome_valide: option }))}
                            className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                              formData.diplome_valide === option
                                ? 'bg-yellow-600 text-white shadow-lg'
                                : 'bg-white border border-gray-200 text-gray-700 hover:bg-yellow-50'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Nouvelles questions sur les projets */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Après la formation</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Depuis la fin de la formation, avez-vous pu avancer dans vos projets ? *
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {agreementOptions.map(option => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, avance_projets: option }))}
                            className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                              formData.avance_projets === option
                                ? 'bg-green-600 text-white shadow-lg'
                                : 'bg-white border border-gray-200 text-gray-700 hover:bg-green-50'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>

                    {formData.avance_projets === 'Oui' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Si oui, comment ?
                        </label>
                        <textarea
                          value={formData.avance_projets_comment}
                          onChange={(e) => {
                            if (e.target.value.length <= 500) {
                              setFormData(prev => ({ ...prev, avance_projets_comment: e.target.value }));
                            }
                          }}
                          maxLength={500}
                          className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                          rows={4}
                          placeholder="Décrivez comment vous avez avancé dans vos projets..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          {formData.avance_projets_comment.length}/500 caractères
                        </p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Est-ce que la formation vous a permis de créer un projet associatif ? *
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {agreementOptions.map(option => (
                          <button
                            key={option}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, creation_projet_associatif: option }))}
                            className={`px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                              formData.creation_projet_associatif === option
                                ? 'bg-green-600 text-white shadow-lg'
                                : 'bg-white border border-gray-200 text-gray-700 hover:bg-green-50'
                            }`}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Où en êtes-vous aujourd'hui ?
                      </label>
                      <textarea
                        value={formData.creation_projet_ou_en_etes_vous}
                        onChange={(e) => {
                          if (e.target.value.length <= 500) {
                            setFormData(prev => ({ ...prev, creation_projet_ou_en_etes_vous: e.target.value }));
                          }
                        }}
                        maxLength={500}
                        className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                        rows={4}
                        placeholder="Décrivez où vous en êtes dans votre projet associatif..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.creation_projet_ou_en_etes_vous.length}/500 caractères
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4">Témoignage</h2>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      En quelques mots, décrivez votre expérience de formation au sein de DKBS
                    </label>
                    <textarea
                      value={formData.temoignage}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setFormData(prev => ({ ...prev, temoignage: e.target.value }));
                        }
                      }}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                      rows={5}
                      placeholder="Partagez votre expérience..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.temoignage.length}/500 caractères
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Boutons de navigation */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
              {currentSection > 1 ? (
                <button
                  type="button"
                  onClick={() => setCurrentSection(prev => prev - 1)}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all flex items-center space-x-2"
                >
                  <i className="ri-arrow-left-line"></i>
                  <span>Précédent</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all flex items-center space-x-2"
                >
                  <i className="ri-arrow-left-line"></i>
                  <span>Retour</span>
                </button>
              )}

              {currentSection < totalSections ? (
                <button
                  type="button"
                  onClick={() => setCurrentSection(prev => prev + 1)}
                  disabled={currentSection === 1 && !formData.apprenant_id}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <span>Suivant</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading || !formData.apprenant_id || !formData.satisfaction_globale_formation || !formData.diplome_valide || !formData.avance_projets || !formData.creation_projet_associatif}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      <span>Envoi...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      <span>Envoyer</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
